// SAB-575: desktop-only inline signup card that occupies the hero's right column.
// Social-first, then an email field whose "Create Account" funnels into the full
// /signup route (where the password is set) — the card itself collects email only,
// matching the mockup. Not rendered on mobile (mobile routes to the full screen).
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useNativeAppVersion } from '@/hooks/useNativeAppVersion';
import { SocialAuthButtons } from './SocialAuthButtons';
import { NATIVE_OAUTH_MIN_VERSION } from '@/lib/nativeOAuthGate';
import { useOAuthSignIn } from '@/hooks/useOAuthSignIn';
import { SIGNUP_CARD_BODY, CARD_ELIGIBILITY_LINE, DEFAULT_AUTHED_HOME } from '@/lib/landingAuthCopy';

export function InlineSignupCard() {
  const navigate = useNavigate();
  const { atLeast } = useNativeAppVersion();
  const [email, setEmail] = useState('');
  // Plain card (no commissioner intent) → existing authed home after OAuth.
  const { signInWith, pending } = useOAuthSignIn(DEFAULT_AUTHED_HOME);

  const goToSignup = (e: React.FormEvent) => {
    e.preventDefault();
    // Carry the typed email into the full signup screen (password is set there).
    navigate('/signup', { state: { email } });
  };

  return (
    <div
      className="rounded-[20px] border p-7"
      style={{ background: 'rgba(255,255,255,0.035)', borderColor: 'rgba(255,255,255,0.11)' }}
    >
      <h2 className="font-varsity uppercase text-[26px] leading-none mb-2">Get in the league</h2>
      <p className="text-[14px] text-muted-foreground leading-[1.5] mb-6">{SIGNUP_CARD_BODY}</p>

      <SocialAuthButtons variant="card" onProvider={signInWith} disabled={pending} />

      {/* SAB (native OAuth gate): gate the "or" divider too so it isn't orphaned when
          SocialAuthButtons returns null on native. On native the email field follows
          the card body directly. */}
      {(!Capacitor.isNativePlatform() || atLeast(NATIVE_OAUTH_MIN_VERSION)) && (
        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.1)' }} />
          <span className="text-xs text-muted-foreground">or</span>
          <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.1)' }} />
        </div>
      )}

      <form onSubmit={goToSignup} className="flex flex-col gap-3">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email address"
          aria-label="Email address"
          className="h-[52px] w-full rounded-xl px-4 text-[15px] text-foreground placeholder:text-muted-foreground/70 outline-none focus:ring-2 focus:ring-ring"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.14)' }}
        />
        <button
          type="submit"
          className="h-[52px] w-full rounded-xl font-display font-bold text-[16px] text-primary-foreground bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.9)] transition-colors"
        >
          Create Account
        </button>
      </form>

      {/* COPY: pending legal (SAB-575) — via landingAuthCopy */}
      <p className="mt-3 text-center text-[12px] text-muted-foreground/70">{CARD_ELIGIBILITY_LINE}</p>

      <p className="mt-3 text-center text-[13px] text-muted-foreground">
        Already have an account?{' '}
        <button type="button" onClick={() => navigate('/login')} className="font-bold text-[hsl(var(--primary))] hover:underline">
          Log In
        </button>
      </p>
    </div>
  );
}

export default InlineSignupCard;
